/**
 * PortalContext - Provides siteId and UDA switching for the public homepage
 * Default site is DTCP (siteId=9). When user clicks a UDA on the map,
 * the siteId changes and all sections reload with that UDA's content.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { UDA_THEMES } from './udaThemes';
import { useAuth } from '../../context/AuthContext';

const DTCP_SITE_ID = 9; // Default DTCP main site

// Public API base - no auth required
const publicApi = axios.create({ baseURL: '/apiv1/public/portalweb' });

interface PortalContextType {
  siteId: number;
  siteName: string;
  /** true when siteId was resolved from the logged-in user's uda_id (vs. map/subdomain browsing) */
  authResolved: boolean;
  setSiteBySlug: (slug: string) => void;
  resetToDefault: () => void;
  publicApi: typeof publicApi;
}

const PortalContext = createContext<PortalContextType>({
  siteId: DTCP_SITE_ID,
  siteName: 'DTCP',
  authResolved: false,
  setSiteBySlug: () => {},
  resetToDefault: () => {},
  publicApi,
});

// UDA code-slug → portal content site mapping. Derived from the canonical UDA
// registry (udaThemes) so there is exactly ONE place to add/edit a UDA. The
// slug is the official UDA code in lowercase (e.g. `eluda`) and is also the
// subdomain label (eluda.dreamstep.in).
const UDA_SITE_MAP: Record<string, { id: number; name: string }> = {
  dtcp: { id: DTCP_SITE_ID, name: 'DTCP' },
  ...Object.fromEntries(
    // A getter: the name is filled from the UDA master after this map is built (applyUdaMasterNames).
    Object.values(UDA_THEMES).map((t) => [t.slug, { id: t.id, get name() { return t.name; } }]),
  ),
};

// Per-UDA hostnames for LOCAL development. Add matching entries to the OS hosts
// file pointing at 127.0.0.1 (see setup-uda-hosts.bat). The first label of the
// hostname is treated as the UDA slug on initial load.
const UDA_HOST_MAP: Record<string, string> = {
  dtcp: 'dtcp.local',
  ...Object.fromEntries(
    Object.values(UDA_THEMES).map((t) => [t.slug, `${t.slug}.local`]),
  ),
};

const slugFromHostname = (): string | null => {
  if (typeof window === 'undefined') return null;
  const first = window.location.hostname.split('.')[0]?.toLowerCase();
  return first && UDA_SITE_MAP[first] ? first : null;
};

// AuthContext only populates `user` after awaiting its token-verify round-trip, so
// on a page load the first render has no user at all. Reading the persisted session
// directly is what keeps a UDA user off the DTCP default for that window — without
// it, every section fires against siteId=9 before the correction lands.
const slugFromStoredSession = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('user_data');
    if (!raw) return null;
    const code = JSON.parse(raw)?.udaCode;
    const slug = typeof code === 'string' ? code.toLowerCase() : null;
    return slug && UDA_SITE_MAP[slug] ? slug : null;
  } catch {
    return null; // unreadable/!JSON session -> behave as an anonymous visitor
  }
};

// Base domain used for local development. Mirrors the deployed pattern exactly:
//   DTCP main → dtcp.local        UDAs → <code>.local   (e.g. buda.local)
// just as prod is dtcp.<domain> / <code>.<domain>. Subdomains work via the
// hosts-file entries added by setup-uda-hosts.bat.
const LOCAL_BASE_DOMAIN = 'local';

/**
 * Resolve the base domain that per-UDA subdomains hang off, for the CURRENT
 * environment. Always returns a base — UDAs are addressed purely by subdomain
 * (there is no ?uda= fallback). The pattern is identical everywhere:
 * UDAs at <code>.<base>, DTCP at dtcp.<base>.
 *   - VITE_PORTAL_BASE_DOMAIN set → use it (UAT/prod, e.g. dreamstep.in)
 *   - on *.local (local dev)      → 'local'  (buda.local, dtcp.local, …)
 *   - any other deployed host     → parent domain of the current host
 *                                   (e.g. buda.dreamstep.in → dreamstep.in)
 */
const getPortalBaseDomain = (): string => {
  if (typeof window === 'undefined') return 'dreamstep.in';

  // 1) Explicit opt-in for deployed environments.
  const envDomain = import.meta.env.VITE_PORTAL_BASE_DOMAIN as string | undefined;
  if (envDomain) return envDomain.replace(/^\.+|\.+$/g, '');

  // 2) Local development against the *.local hosts entries.
  const { hostname } = window.location;
  if (hostname === LOCAL_BASE_DOMAIN || hostname.endsWith('.' + LOCAL_BASE_DOMAIN)) {
    return LOCAL_BASE_DOMAIN;
  }

  // 3) Any other host: derive the parent domain by dropping the first label
  //    (sub.parent.tld → parent.tld) so we always emit a sibling subdomain.
  const parts = hostname.split('.');
  return parts.length > 2 ? parts.slice(1).join('.') : hostname;
};

/**
 * Build the URL for a UDA's microsite. UDAs are ALWAYS opened on their own
 * subdomain (no ?uda= fallback):
 *   local (hosts set) → http://<code>.local:3000/
 *   UAT / prod        → https://<code>.<base-domain>/
 */
export const getUdaUrl = (slug: string): string => {
  const base = getPortalBaseDomain();
  const { protocol, port } = window.location;
  const portPart = port ? `:${port}` : '';
  return `${protocol}//${slug}.${base}${portPart}/`;
};

/**
 * URL of the main DTCP portal. DTCP always lives at the `dtcp` subdomain of the
 * base (dtcp.local locally, dtcp.<base> on a deployed domain). Used by
 * "Back to DTCP".
 */
export const getDtcpUrl = (): string => {
  const base = getPortalBaseDomain();
  const { protocol, port } = window.location;
  const portPart = port ? `:${port}` : '';
  return `${protocol}//dtcp.${base}${portPart}/`;
};

export const PortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // The active UDA comes from the subdomain in the address bar. ?uda= is READ
  // only as a fallback for the admin CMS preview (iframe, same-host) — the
  // public map never writes it; it always opens subdomains.
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // An explicit subdomain or ?uda= is a deliberate override and always wins.
  // Without one, we fall back to DTCP first, then (below) to the logged-in
  // user's own UDA site once it resolves.
  const explicitSlug = slugFromHostname() || searchParams.get('uda');
  // The logged-in user's own UDA code (e.g. 'RUDA') maps straight to their site
  // via the same registry the subdomains use, falling back to the persisted session
  // while AuthContext verifies — so a UDA user never renders (or fetches) against
  // the DTCP default first.
  const authSlug = (user?.udaCode ? user.udaCode.toLowerCase() : null) ?? slugFromStoredSession();
  const initial = explicitSlug
    ? (UDA_SITE_MAP[explicitSlug.toLowerCase()] || UDA_SITE_MAP.dtcp)
    : (authSlug && UDA_SITE_MAP[authSlug]) || UDA_SITE_MAP.dtcp;

  const [siteId, setSiteId] = useState(initial.id);
  const [siteName, setSiteName] = useState(initial.name);
  // true only while siteId reflects the logged-in user's UDA (not map/subdomain browsing)
  const [authResolved, setAuthResolved] = useState(!explicitSlug && !!authSlug);

  // Scope the home to the logged-in user's UDA: resolve uda_id -> portal site.
  // State users (no udaId) and anonymous visitors keep the DTCP default.
  // Skipped entirely when an explicit subdomain/?uda= override is present.
  useEffect(() => {
    if (explicitSlug) return;

    // 1) Resolve from the user's UDA code immediately. This needs no request, so
    //    it holds even if udaId is missing from the session.
    const local = authSlug ? UDA_SITE_MAP[authSlug] : undefined;
    if (local) {
      setSiteId(local.id);
      setSiteName(local.name);
      setAuthResolved(true);
    }

    // 2) Refine against the DB, which is authoritative for uda_id -> site.
    const udaId = user?.udaId;
    if (!udaId || udaId <= 0) {
      if (!local) setAuthResolved(false); // genuinely not a UDA user -> stay on DTCP
      return;
    }
    let cancelled = false;
    publicApi.get(`/site-by-uda/${udaId}`)
      .then((res) => {
        const site = res.data?.data;
        if (!cancelled && site && site.prvdr_ste_id) {
          setSiteId(Number(site.prvdr_ste_id));
          setSiteName(site.prvdr_ste_nm || 'UDA');
          setAuthResolved(true);
        }
      })
      .catch(() => { /* keep whatever step 1 resolved */ });
    return () => { cancelled = true; };
  }, [user?.udaId, authSlug, explicitSlug]);

  // In-app (same-tab) switch — state only. Primary navigation opens the UDA's
  // own subdomain in a new tab via getUdaUrl(); this keeps the API consistent.
  const setSiteBySlug = useCallback((slug: string) => {
    const entry = UDA_SITE_MAP[slug.toLowerCase()];
    if (entry) {
      setSiteId(entry.id);
      setSiteName(entry.name);
      setAuthResolved(false); // manual navigation -> browse mode (themed UdaLanding)
    }
  }, []);

  // "Back to DTCP" navigates to the DTCP host (we're on a UDA subdomain).
  const resetToDefault = useCallback(() => {
    window.location.assign(getDtcpUrl());
  }, []);

  return (
    <PortalContext.Provider value={{ siteId, siteName, authResolved, setSiteBySlug, resetToDefault, publicApi }}>
      {children}
    </PortalContext.Provider>
  );
};

export const usePortal = () => useContext(PortalContext);
export { UDA_SITE_MAP, UDA_HOST_MAP };
